import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { CheckIcon, SpinnerIcon, AlertIcon } from './icons';
import type { OnboardingStatus, TimeWindow, WorkflowStatusView } from './types';

const POLL_MS = 5000;

const TIME_WINDOWS: { id: TimeWindow; label: string; }[] = [
  { id: 'all_time', label: 'All Time' },
  { id: '6_months', label: 'Last 6 Months' },
  { id: '12_months', label: 'Last 12 Months' },
  { id: '2_years', label: 'Last 2 Years' },
  { id: '5_years', label: 'Last 5 Years' },
];

/**
 * Step 3 — import rules + start the background historical import.
 *
 * The import is driven by a Cloudflare Workflows instance (M13) that
 * self-drives customers → subscriptions → invoices → disputes → aggregates →
 * done. Progress is read from GET /onboarding/bootstrap/workflow-status, which
 * merges the platform instance status with the DO's live progress row (M13
 * T7). That is the KEY to QA #3: the instance keeps running independently of
 * the UI, so navigating away and returning shows live progress — no local
 * "importStarted" flag whose loss hides the running import.
 *
 * On mount the step probes workflow-status. If an import is already running,
 * it jumps straight to the progress view (covers leave-and-return + a refresh
 * mid-import). Deal-mirroring (createDeals) is configured on the preceding
 * "Sync settings" step (M14 QA #18); this step only persists it at start so the
 * HubSpot pipelines are provisioned (POST onboarding/provision).
 */
export function StepImport({
  status,
  onProgressVisibleChange,
  onBack,
}: {
  status: OnboardingStatus | null;
  /** Notified when the progress view becomes visible/hidden. The wizard uses
   *  this to advance the step INDICATOR to 'done' (so "Import rules" greens and
   *  "Done" highlights blue) while the progress view still renders here.
   *  QA round 2 #1. NOTE (review r4): fires only for RUNNING/COMPLETED imports
   *  — a FAILED import keeps the indicator on 'import' so users aren't shown a
   *  highlighted "Done" tab above a failure screen (the round-4 reviewer
   *  confusion: dead-end + contradictory state). */
  onProgressVisibleChange?: (visible: boolean) => void;
  /** Navigate back to the products step (rules form only — hidden once the
   *  import has started; the progress view has its own Retry affordance). */
  onBack?: () => void;
}) {
  // Deal mirroring is decided on the preceding Sync-settings step; read the
  // persisted value so pipeline provisioning matches it at start. Defaults on
  // (the tenant-wide default) when unknown.
  const createDeals = status?.createDeals ?? true;
  const [timeWindow, setTimeWindow] = useState<TimeWindow>('all_time');
  const [includeCanceled, setIncludeCanceled] = useState(false);
  const [includeAbandoned, setIncludeAbandoned] = useState(false);

  const [workflowStatus, setWorkflowStatus] = useState<WorkflowStatusView | null>(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Pipeline-cap fallbacks from provisioning (M31): informational, non-fatal.
  const [provisionWarnings, setProvisionWarnings] = useState<string[]>([]);
  // Latest status in a ref so the poller's error-retry path reads current
  // state (not the stale initial-render closure).
  const statusRef = useRef<WorkflowStatusView | null>(null);
  // Refetched on demand (after start + on the poll cadence).
  const refreshStatus = useCallback(async () => {
    const data = await api.get<WorkflowStatusView>('onboarding/bootstrap/workflow-status');
    statusRef.current = data;
    setWorkflowStatus(data);
    return data;
  }, []);

  // Initial probe + polling while the import is non-terminal. Mounting this
  // step always re-checks the instance, so a user returning mid-import sees
  // live progress rather than the import-rules form.
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = async () => {
      try {
        const data = await refreshStatus();
        if (cancelled) return;
        if (isRunning(data)) {
          timer = setTimeout(tick, POLL_MS);
        }
      } catch {
        // Best-effort — retry on the next cadence if the last known state
        // was still running (read from the ref, not the stale closure).
        const last = statusRef.current;
        if (!cancelled && last && isRunning(last)) {
          timer = setTimeout(tick, POLL_MS);
        }
      }
    };
    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStart = async () => {
    setError(null);
    setStarting(true);
    try {
      // 1. Provision pipelines for deal mirroring (createDeals) so the import
      //    creates Deals in the right pipelines. No-op beyond the D1 toggle
      //    when createDeals is false. May return warnings (plan pipeline-cap
      //    fallbacks) — non-fatal, shown under the progress view.
      //
      //    Review r6: the server's last-resort fallback returns 502 with
      //    { createDeals: false } after it has ALREADY disabled deal mirroring
      //    and stamped the tenant ("customers, subscriptions, and invoices
      //    will still sync"). That is a documented degradation, not a stop
      //    condition — api.post throws on any non-2xx, so we must catch it
      //    HERE and continue to the import instead of dead-ending the wizard
      //    (the old behavior: error banner + no path to start the import).
      let warnings: string[] = [];
      try {
        const provisioned = await api.post<{
          warnings?: string[];
          createDeals?: boolean;
        }>('onboarding/provision', { createDeals });
        warnings = provisioned?.warnings ?? [];
      } catch (e) {
        const errBody = e instanceof ApiError ? e.body : null;
        const degraded =
          errBody !== null &&
          typeof errBody === 'object' &&
          (errBody as { createDeals?: unknown }).createDeals === false;
        if (degraded) {
          // Degraded server-side: deal mirroring is off, sync continues.
          // Surface the server's explanation as a warning under the progress
          // view and proceed to start the import.
          const serverMsg = (errBody as { error?: unknown }).error;
          warnings = [
            typeof serverMsg === 'string'
              ? serverMsg
              : 'Deal mirroring was disabled for this workspace; everything else will sync normally.',
          ];
        } else {
          throw e; // transient/unknown — surface as a blocking error (retryable)
        }
      }
      setProvisionWarnings(warnings);
      // 2. Start the bootstrap import (creates the Workflow instance).
      await api.post('onboarding/bootstrap', { timeWindow, includeCanceled, includeAbandoned });
      // 3. Immediately probe so the progress view appears without waiting a tick.
      await refreshStatus();
    } catch (e) {
      setError(
        e instanceof ApiError ? e.message : 'Failed to start the import.'
      );
    } finally {
      setStarting(false);
    }
  };

  // Show the progress view whenever there's a non-missing instance (running,
  // complete, or failed). Missing + nothing started → the rules form.
  const progressVisible = !!(workflowStatus && !workflowStatus.missing);
  // Indicator-advance only when NOT failed (review r4 — see prop doc above).
  const failedNow =
    !!workflowStatus &&
    !workflowStatus.missing &&
    (workflowStatus.status === 'errored' ||
      workflowStatus.status === 'terminated');
  const indicatorAdvance = progressVisible && !failedNow;
  // Notify the wizard so the step INDICATOR advances to 'done' (Import rules
  // greens, Done highlights) while the progress view renders here (QA #1).
  useEffect(() => {
    onProgressVisibleChange?.(indicatorAdvance);
    return () => onProgressVisibleChange?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indicatorAdvance]);

  if (progressVisible && workflowStatus) {
    return (
      <div className="space-y-3">
        {provisionWarnings.length > 0 && (
          <div className="rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-800">
            {provisionWarnings.map((w) => (
              <p key={w}>⚠ {w}</p>
            ))}
          </div>
        )}
        <ProgressView status={workflowStatus} onRetry={handleStart} retrying={starting} />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-ink-900">Import rules</h2>
        <p className="mt-1 text-sm text-ink-500">
          Configure how your Stripe history is imported into HubSpot. The import
          runs in the background — you don't need to review each record.
        </p>
      </div>

      {/* Time window */}
      <div className="card p-5">
        <h3 className="font-semibold text-ink-900">Time window</h3>
        <p className="mt-1 text-sm text-ink-500">
          How far back to import subscriptions from your Stripe history.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {TIME_WINDOWS.map((w) => (
            <button
              key={w.id}
              onClick={() => setTimeWindow(w.id)}
              className={[
                'rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors',
                timeWindow === w.id
                  ? 'border-brand-500 bg-brand-50 text-brand-700'
                  : 'border-ink-200 bg-white text-ink-600 hover:bg-ink-50',
              ].join(' ')}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      {/* Subscription state scope */}
      <div className="card p-5">
        <h3 className="font-semibold text-ink-900">Subscription state scope</h3>
        <p className="mt-1 text-sm text-ink-500">Which subscription statuses to import.</p>
        <div className="mt-3 space-y-2.5">
          <ScopeRow
            label="Ongoing base"
            description="Active, Trialing, Past Due, Paused, Unpaid, Incomplete."
            checked
            locked
          />
          <ScopeRow
            label="Include canceled / churned"
            description="Canceled subscriptions. Import as Closed Won deals with status 'canceled'. Useful for historical revenue analysis."
            checked={includeCanceled}
            onChange={setIncludeCanceled}
          />
          <ScopeRow
            label="Include abandoned checkouts"
            description="Incomplete-expired subscriptions (checkouts that never completed). Usually noise — off by default."
            checked={includeAbandoned}
            onChange={setIncludeAbandoned}
          />
        </div>
      </div>

      {/* Customer matching — fixed policy, informational */}
      <div className="card p-5">
        <h3 className="font-semibold text-ink-900">Customer matching</h3>
        <ul className="mt-2 space-y-1.5 text-sm text-ink-600">
          <FeatureLine>Customers auto-linked to HubSpot contacts by email address.</FeatureLine>
          <FeatureLine>Unmatched customers create new HubSpot contacts automatically.</FeatureLine>
          <FeatureLine>Historical invoices backfilled; contact/company LTV aggregates computed.</FeatureLine>
          {createDeals && (
            <FeatureLine>Subscriptions imported as Closed Won deals with line items.</FeatureLine>
          )}
        </ul>
      </div>

      {error && (
        <p className="rounded-lg bg-danger-500/10 px-3 py-2 text-sm text-danger-600">
          {error}
        </p>
      )}

      <div className="flex items-center gap-3">
        {onBack && !progressVisible && (
          <button
            type="button"
            onClick={onBack}
            className="text-sm font-medium text-ink-400 transition-colors hover:text-ink-700"
          >
            ← Back
          </button>
        )}
        <button className="btn-primary" onClick={handleStart} disabled={starting}>
          {starting ? (
            <>
              <SpinnerIcon className="h-4 w-4" /> Starting…
            </>
          ) : (
            'Start import'
          )}
        </button>
      </div>
      <p className="text-xs text-ink-400">
        You can safely close this window — the import continues in the background.
      </p>
    </div>
  );
}

/** One subscription-scope checkbox row. `locked` renders a disabled, checked row. */
function ScopeRow({
  label,
  description,
  checked,
  locked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  locked?: boolean;
  onChange?: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3">
      <input
        type="checkbox"
        checked={checked}
        disabled={locked}
        onChange={(e) => onChange?.(e.target.checked)}
        className="mt-1 h-4 w-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500 disabled:text-ink-400"
      />
      <span>
        <span className="font-medium text-ink-800">
          {label}
          {locked && <span className="ml-1.5 text-xs text-ink-400">(always on)</span>}
        </span>
        <span className="block text-sm text-ink-500">{description}</span>
      </span>
    </label>
  );
}

function FeatureLine({ children }: { children: React.ReactNode; }) {
  return (
    <li className="flex items-start gap-2">
      <CheckIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-success-600" />
      <span>{children}</span>
    </li>
  );
}

/** Import phases in order (mirrors BootstrapPhase on the Worker). The two
 *  invoice sub-phases are grouped into one "Invoices" step for the UI. */
// Mirrors the workflow execution order in historicalSync.ts:
// customers → subscriptions → invoices → charges → disputes → aggregates → done.
// 'charges' is a real phase (charges ledger + refunds, M12) — it MUST be in the
// stepper, otherwise mid-charges the stepper jumps backwards (phaseIndex falls
// back to 0 for an unknown phase).
const PHASE_ORDER = [
  'customers',
  'subscriptions',
  'subscription_invoices',
  'standalone_invoices',
  'charges',
  'refunds',
  'disputes',
  'done',
] as const;

const PHASE_LABELS: Record<string, string> = {
  customers: 'Customers',
  subscriptions: 'Subscriptions',
  subscription_invoices: 'Invoices',
  standalone_invoices: 'Invoices',
  charges: 'Charges',
  refunds: 'Refunds',
  disputes: 'Disputes',
  done: 'Done',
};

/** A Workflow instance is still advancing (the poller should keep ticking). */
function isRunning(s: WorkflowStatusView): boolean {
  return (
    s.status === 'running' ||
    s.status === 'queued' ||
    s.status === 'waiting' ||
    s.status === 'waitingForPause' ||
    s.status === 'unknown'
  );
}

/** A Workflow instance reached a terminal state (success or failure). */
function isTerminal(s: WorkflowStatusView): boolean {
  return s.status === 'complete' || s.status === 'errored' || s.status === 'terminated';
}

/** Map a phase string to its index in PHASE_ORDER (for the stepper). */
function phaseIndex(phase: string): number {
  const i = PHASE_ORDER.indexOf(phase as (typeof PHASE_ORDER)[number]);
  return i === -1 ? 0 : i;
}

/**
 * Progress view shown while the import runs / once it finishes. Reads the M13
 * workflow-status payload: a phase stepper (Customers → Subscriptions →
 * Invoices → Disputes → Done), running processed/failed counts, a throughput-
 * based ETA, and real error surfacing (a failed step shows its message rather
 * than an opaque spinner).
 */
function ProgressView({
  status,
  onRetry,
  retrying,
}: {
  status: WorkflowStatusView;
  /** Review r4 dead-end fix: retry a failed import. The start endpoint
   *  force-recreates the Workflow instance and every phase is idempotent
   *  (skip-mapped customers, INSERT OR REPLACE, cursor resume) — a re-run
   *  continues from where the failed one died, without duplicates. */
  onRetry?: () => void;
  retrying?: boolean;
}) {
  const phase = status.progress?.phase ?? 'idle';
  const terminal = isTerminal(status);
  // Done when the instance completed cleanly OR the DO row reached 'done'.
  const completed = status.status === 'complete' || phase === 'done';
  const failed =
    status.status === 'errored' ||
    status.status === 'terminated' ||
    (status.progress?.errorMessage != null && terminal);

  const totalProcessed = status.progress?.totalProcessed ?? 0;
  const totalFailed = status.progress?.totalFailed ?? 0;

  // Stepper progress: fraction of phases complete (clamped 0..1).
  // (ETA was removed — the throughput heuristic was unreliable across phases of
  // differing volume. The stepper + counts are honest progress. QA round 2 #2.)
  const pct = completed ? 1 : Math.min(phaseIndex(phase) / PHASE_ORDER.length, 0.95);

  // The message to surface for a failed import.
  const errorMsg =
    status.error?.message ??
    status.progress?.errorMessage ??
    (failed ? 'The import failed unexpectedly.' : null);

  return (
    <div className="card p-6">
      <h2 className="text-lg font-semibold text-ink-900">
        {completed
          ? 'Import complete'
          : failed
            ? 'Import failed'
            : 'Import in progress…'}
      </h2>

      {/* Phase stepper */}
      <ol className="mt-5 flex flex-wrap items-center gap-2">
        {PHASE_ORDER.filter((p) => p !== 'standalone_invoices').map((p) => {
          const current = phase === p || (p === 'subscription_invoices' && phase === 'standalone_invoices');
          const past = phaseIndex(phase) > phaseIndex(p) || completed;
          return (
            <li key={p} className="flex items-center gap-2">
              <span
                className={[
                  'flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors',
                  completed || past
                    ? 'bg-success-500/10 text-success-600'
                    : current
                      ? 'bg-brand-600 text-white'
                      : 'bg-ink-100 text-ink-400',
                ].join(' ')}
              >
                <span
                  className={[
                    'flex h-4 w-4 items-center justify-center rounded-full text-[10px]',
                    completed || past
                      ? 'bg-success-500 text-white'
                      : current
                        ? 'bg-white/25'
                        : 'bg-ink-300 text-white',
                  ].join(' ')}
                >
                  {completed || past ? <CheckIcon className="h-2.5 w-2.5" /> : ''}
                </span>
                {PHASE_LABELS[p]}
              </span>
            </li>
          );
        })}
      </ol>

      {/* Progress bar (phase-derived, not a fake 60%) */}
      <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-ink-100">
        <div
          className={[
            'h-full rounded-full transition-all',
            completed ? 'bg-success-500' : failed ? 'bg-danger-500' : 'bg-brand-600',
          ].join(' ')}
          style={{ width: `${Math.round((failed ? phaseIndex(phase) : pct) * 100)}%` }}
        />
      </div>

      {/* Counts. Phase is already shown by the stepper above; ETA is intentionally
          omitted (the heuristic was unreliable across phases of differing volume —
          honest counts + the stepper are more useful than a misleading ETA).
          QA round 2 #2/#4. */}
      <dl className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
        <Stat label="Processed" value={totalProcessed.toLocaleString()} />
        <Stat label="Failed" value={totalFailed.toLocaleString()} tone={totalFailed > 0 ? 'warn' : 'default'} />
        <Stat label="Status" value={completed ? 'Complete' : failed ? 'Failed' : 'Running'} />
      </dl>

      {/* Error surfacing (QA #3: a failed step shows its message) */}
      {failed && errorMsg && (
        <div className="mt-4 flex items-start gap-2 rounded-lg bg-danger-500/10 px-3 py-2 text-sm text-danger-600">
          <AlertIcon className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span className="break-words">{errorMsg}</span>
        </div>
      )}

      <p className="mt-4 text-sm text-ink-500">
        {completed
          ? 'Your Stripe history has been imported into HubSpot.'
          : failed
            ? 'The import stopped before finishing. Already-imported records are kept — retrying continues from where it stopped.'
            : 'You can close this window — the import continues in the background. Reopen this page later to check progress.'}
      </p>
      {/* On completion, go straight to the dashboard (QA round 2 #5) — no
          separate "Done" screen. While running there's nothing to continue to;
          a FAILED import gets a Retry CTA (review r4 dead-end fix — the start
          endpoint force-recreates the instance; phases are idempotent). */}
      {completed && (
        <div className="mt-4 flex flex-wrap gap-3">
          <Link to="/dashboard" className="btn-primary">
            Go to dashboard
          </Link>
          <a
            href="https://app.hubspot.com"
            target="_blank"
            rel="noreferrer"
            className="btn-secondary"
          >
            Open HubSpot
          </a>
        </div>
      )}
      {failed && onRetry && (
        <div className="mt-4">
          <button className="btn-primary" onClick={onRetry} disabled={retrying}>
            {retrying ? (
              <span className="inline-flex items-center gap-2">
                <SpinnerIcon className="h-4 w-4" /> Retrying…
              </span>
            ) : (
              'Retry import'
            )}
          </button>
        </div>
      )}
    </div>
  );
}

/** A small stat tile for the progress grid. */
function Stat({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: string;
  tone?: 'default' | 'warn';
}) {
  return (
    <div className="rounded-lg bg-ink-50 px-3 py-2">
      <dt className="text-xs uppercase tracking-wide text-ink-400">{label}</dt>
      <dd
        className={[
          'font-medium',
          tone === 'warn' ? 'text-warn-600' : 'text-ink-800',
        ].join(' ')}
      >
        {value}
      </dd>
    </div>
  );
}
